(ns app.front.form.add
  (:require [app.domain.group.model :as group-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.group.admin.form-schemas.util :as form-schemas-util]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-fx ::start-form-add
  (fn [{:keys [db]} [_ authority-group-id]]
    {:db           (-> db
                       (assoc ::loading-form-schemas? true))
     :comm/command {:command    :form/get-schemas
                    :method     :get
                    :data       (util/mmap authority-group-id)
                    :on-success [::schemas-fetched]}}))

(rf/reg-sub ::add-form-modal-open?
  (fn [db _]
    (get db ::add-form-modal-open? false)))

(defn- scroll-section-id [{entity-type :entity/type}]
  (case entity-type
    :entity.type/pre-consultation :pre-consultation-forms
    :entity.type/execution        :code-enforcement-forms
    :application-data))

(rf/reg-event-fx ::trigger-add-form
  (fn [{:keys [db]} [_ process-target]]
    (let [auth-group-id (-> process-target group-model/get-authority-group :db/id)]
      {:db                       (assoc db ::add-form-modal-open? true)
       :fx                       [[:dispatch [::start-form-add auth-group-id]]]
       ::futil/scroll-to-section (scroll-section-id process-target)})))

(rf/reg-event-db ::close-add-form-modal
  (fn [db _]
    (assoc db ::add-form-modal-open? false)))

(rf/reg-event-fx ::schemas-fetched
  (fn [{:keys [db]} [_ status resp]]
    {:dispatch [::entities/merge-entity-db status (-> resp du/entities->entity-db)]
     :db       (-> db (dissoc ::loading-form-schemas?))}))

(rf/reg-sub ::form-schemas
  (fn [[_ process-target-id authority-group-id]]
    [(rf/subscribe [::entities/entities {:entity/type :entity.type/form-schema}])
     (rf/subscribe [::entities/entity authority-group-id])
     (rf/subscribe [::entities/entity process-target-id])
     (rf/subscribe [::process/entity-process process-target-id])])
  (fn [[schemas authority-group target-entity process] [_ _ authority-group-id]]
    (let [authority-schemas   (->> schemas
                                   (filter (fn [{{group-id :db/id} :domain/authority-group}]
                                             (= group-id authority-group-id))))
          superdomain         (group-model/group-superdomain authority-group)
          superdomain-schemas (when (and superdomain (ff/enabled? :feature-flag.id/superdomain-can-manage-forms))
                                (->> schemas
                                     (filter (fn [{{group-id :db/id} :domain/authority-group}]
                                               (-> superdomain :db/id (= group-id))))
                                     (map (fn [schema]
                                            (form-schemas-util/rename-inherited-form-schema schema superdomain)))))]
      (->> (concat authority-schemas superdomain-schemas)
           (filter (fn [{schema-type :form.schema/type}]
                     (case (:entity/type target-entity)
                       :entity.type/application (or (nil? schema-type) (= schema-type :form.schema.type/general))
                       :entity.type/pre-consultation (= schema-type :form.schema.type/pre-consultation)
                       :entity.type/execution (= schema-type :form.schema.type/execution)
                       :entity.type/periodic-parent (= schema-type :form.schema.type/general)
                       false)))
           (filter #(process/signal-permitted? process [:process.signal/add-form {:form-schema-id (:db/id %)}]))
           (sort-by :form.schema/name)))))

(rf/reg-sub ::loading?
  :-> ::loading-form-schemas?)

(rf/reg-event-db ::set-selected-schema
  (fn [db [_ schema-id]]
    (assoc db ::selected-schema schema-id)))

(rf/reg-sub ::selected-schema-id
  :-> ::selected-schema)

(rf/reg-sub ::selected-schema
  :<- [::selected-schema-id]
  :<- [::entities/context]
  (fn [[schema-id ctx] _]
    (entities/find-entity ctx schema-id)))

(rf/reg-sub ::can-submit?
  :<- [::selected-schema]
  :-> some?)

(rf/reg-event-fx ::do-add-form
  (fn [{:keys [db]} [_ process-target-id form-schema-id review-id]]
    (let [signal (if review-id
                   :process.signal/add-form-to-review
                   :process.signal/add-form)]
      {:dispatch [::process/signal
                  process-target-id
                  [signal (merge {:form-schema-id form-schema-id}
                                 (when review-id
                                   {:review-id review-id}))]]
       :db       (dissoc db ::selected-schema)})))

(defn add-modal-content [{:keys [tr process-target-id authority-group-id]}]
  [:div
   (if (<sub [::loading?])
     (cc/loading-spinner {:with-overlay false})
     [ui-select/select
      {:id           "add-form-select-schema"
       :label-text   (tr [:add-form/select-form-schema])
       :required     true
       :allow-empty? false
       :val->str     :form.schema/name
       :value        (<sub [::selected-schema])
       :tr           tr
       :on-change    (fn [{form-schema-id :db/id}]
                       (>evt [::set-selected-schema form-schema-id]))
       :values       (<sub [::form-schemas process-target-id authority-group-id])}])])


(defn add-form-modal
  "Standalone modal for add-form, triggered from global options panel or section menu"
  [{:keys [tr process-target]}]
  (let [modal-open?        (<sub [::add-form-modal-open?])
        authority-group-id (-> process-target
                               group-model/get-authority-group
                               :db/id)
        process-target-id  (:db/id process-target)]
    (when modal-open?
      [ui/confirm-modal
       {:id              "add-form-modal"
        :tr              tr
        :open?           true
        :on-close        #(>evt [::close-add-form-modal])
        :modal-title     (tr [:form/add-form-title])
        :yes-disabled?   (not (<sub [::can-submit?]))
        :yes-button-text (tr [:add])
        :no-button-text  (tr [:cancel])
        :on-yes-click    #(do
                            (>evt [::do-add-form process-target-id (<sub [::selected-schema-id]) nil])
                            (>evt [::close-add-form-modal]))}
       [add-modal-content (util/mmap tr process-target-id authority-group-id)]])))


(defn add-button [{:keys [process-target-id authority-group-id opts button-text add-text cancel-text review-id]}]
  (let [signal (if review-id
                 [:process.signal/add-form-to-review {:review-id review-id}]
                 [:process.signal/add-form])]
    (when (<sub [::process/signal-permitted? process-target-id signal])
      (let [tr (<sub [:tr])]
        [ui/modal-confirmation-button
         {:id                  "add-form-button"
          :modal-title         (tr [:form/add-form-title])
          :on-modal-open       #(>evt [::start-form-add authority-group-id])
          :tr                  tr
          :trigger-button-opts opts
          :trigger-button-text button-text
          :yes-disabled?       (not (<sub [::can-submit?]))
          :on-yes-click        #(>evt [::do-add-form process-target-id (<sub [::selected-schema-id]) review-id])
          :no-button-text      cancel-text
          :yes-button-text     add-text}
         [add-modal-content (util/mmap tr process-target-id authority-group-id)]]))))
